import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Sparkles, CheckCircle2 } from "lucide-react";

export default function PartCard({
  part,
  onSelect,
  isSelected = false,
  onFoundIt,
  showSuccess = false,
  // showQuantities = false, // Kept for future use if needed
}) {
  const getCategoryColor = () => {
    const partName = (part['Bolt Size'] || '').toLowerCase();
    if (partName.includes('cable')) return 'from-purple-500 to-purple-600';
    if (partName.includes('mcu') || partName.includes('pcb') || partName.includes('led')) return 'from-teal-500 to-teal-600';
    if (partName.includes('bolt') || partName.includes('nut') || partName.includes('washer') || partName.includes('screw')) return 'from-blue-500 to-blue-600';
    return 'from-gray-500 to-gray-600';
  };

  const categoryColor = getCategoryColor();

  const cardStyle = {
    zIndex: isSelected ? 10 : 0,
    position: isSelected ? 'relative' : 'static', // Explicit positioning for zIndex
  };

  const buttonStyle = isSelected ? {
    willChange: 'transform, height, padding-bottom',
  } : {};

  return (
    <motion.div
      layout
      style={cardStyle} // Apply dynamic style
      animate={{
        scale: isSelected ? 1.02 : 1,
        // zIndex is now in style to ensure it applies with position
      }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 30,
        // layout: { duration: 0.3 }, // REMOVED for smoother spring
      }}
      className="relative" // Keep relative for inner absolute elements
    >
      <motion.button
        layout // Animate layout changes (e.g., padding)
        style={buttonStyle} // Apply dynamic style for will-change
        whileHover={{ scale: isSelected ? 1 : 1.01 }}
        whileTap={{ scale: isSelected ? 1 : 0.99 }}
        onClick={() => onSelect(part)}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 30,
          // layout: { duration: 0.3 }, // REMOVED for smoother spring
        }}
        className={`w-full text-left p-4 ${isSelected ? "pb-20" : ""} bg-white rounded-2xl ${
          isSelected
            ? "shadow-[0_4px_20px_rgba(0,0,0,0.08)] ring-2 ring-blue-400 ring-inset"
            : "shadow-[0_2px_10px_rgba(0,0,0,0.03)] hover:shadow-[0_4px_20px_rgba(0,0,0,0.05)]"
        } transition-all duration-300 focus:outline-none`}
      >
        <div className="flex justify-between items-start"> {/* Changed items-center to items-start */}
          <div className="flex-grow mr-2"> {/* Added flex-grow and mr-2 for better spacing */}
            {isSelected && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2 }}
                className="mb-1"
              >
                <span className="text-xs font-medium text-gray-500">Selected Part</span>
              </motion.div>
            )}
            <motion.h3 layout className={`font-medium text-gray-900 ${isSelected ? "text-xl" : "text-base"}`}>
              {part['Bolt Size']}
            </motion.h3>
            {/* Assuming part.category might exist, or infer from name if needed */}
            {/* <span className="text-xs text-gray-500">{part.category || 'Unknown Category'}</span> */}
            {/* Placeholder for quantities if that prop is used */}
            {/* {showQuantities && part.quantity !== undefined && (
              <div className="text-xs text-gray-500 mt-1">
                Quantity: <span className="font-medium">{part.quantity}</span>
              </div>
            )} */}
          </div>
          <div
            className={`flex-shrink-0 bg-gradient-to-r ${categoryColor} text-white px-3 py-1 rounded-full text-xs flex items-center gap-1`}
          >
            <MapPin className="h-3 w-3" />
            {part['Bin Number']}
          </div>
        </div>

        {/* Found It button for selected part, positioned absolutely within the card */}
        {isSelected && onFoundIt && (
          <motion.button
            layout
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }} // Animate out if card is deselected
            transition={{ delay: 0.1, type: "spring", stiffness: 300, damping: 25 }}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={(e) => {
              e.stopPropagation(); // Important: Prevent card's onSelect from firing again
              onFoundIt();
            }}
            className="absolute bottom-4 left-4 right-4 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white py-3 px-4 rounded-xl text-sm font-medium flex items-center justify-center gap-2 shadow-md"
          >
            <Sparkles className="h-4 w-4" /> {/* Adjusted icon size */}
            <span>Found It!</span>
          </motion.button>
        )}
      </motion.button>

      {/* Success animation overlay */}
      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }} // Quick fade for overlay
            className="absolute inset-0 bg-gradient-to-br from-green-500 to-emerald-500 flex flex-col items-center justify-center z-20 rounded-2xl"
          >
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 300, damping: 20, delay: 0.1 }} // Icon animates after overlay
            >
              <CheckCircle2 className="h-16 w-16 text-white" />
            </motion.div>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 20, delay: 0.2 }} // Text animates after icon
              className="text-white font-medium text-lg text-center mt-2"
            >
              Found It!
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
} 